import {
  FundNoteAdd,
  FundNoteBuy,
  MONEY_MARKET_EVENT_GATE,
  MONEY_MARKET_EVENT_FEE,
  MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
  RETIREMENT,
} from 'src/settings/settings';
import {
  DISABLE_ON_MATCH,
  updateFundNote,
} from '../shared/shared-fund-notes-utility';

/**
 * Updates the passed in ALL fund object with applicable notes and disables as appropriate.
 *
 * @param {AllFund} fund
 * @param {UserFund[]} newFunds
 * @param {object} selectedRegistrationType
 * @returns {AllFund} Same fund object is returned to simplify chaining
 */
export function updateSharedAddFundNotesForFund(
  fund,
  newFunds,
  selectedRegistrationType
) {
  if (!selectedRegistrationType) {
    return fund;
  }

  const { agreementType = '', existingTickers = [] } = selectedRegistrationType;

  updateFundNote(
    fund,
    existingTickers.includes(fund.ticker),
    FundNoteAdd.OWNED_EXISTING,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    newFunds &&
      newFunds.some(
        (newFund) =>
          newFund.ticker === fund.ticker &&
          newFund.agreementType === agreementType
      ),
    FundNoteAdd.OWNED_NEW,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_GATE_ACTIVE,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_FEE_ACTIVE
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_GATE_REMOVED
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_FEE_REMOVED
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_GATE_ACTIVE,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_FEE_ACTIVE
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_GATE_REMOVED
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteBuy.MONEY_MARKET_EVENT_FEE_REMOVED
  );

  return fund;
}

/**
 * Updates the passed in ALL fund object with applicable notes and disables as appropriate.
 *
 * @param {AllFund} fund
 * @param {UserFund[]} newFunds
 * @param {object} selectedRegistrationType
 * @returns {AllFund} Same fund object is returned to simplify chaining
 */
export function updateAddFundNotesForFund(
  fund,
  newFunds,
  selectedRegistrationType
) {
  if (!selectedRegistrationType) {
    return fund;
  }

  const { agreementType = '', classification = '' } = selectedRegistrationType;

  const selectedAgreementIsIRA = agreementType.includes('IRA');

  updateFundNote(
    fund,
    (fund.ticker === 'TRLAX' ||
      fund.ticker === 'TRABX' ||
      fund.ticker === 'TRAVX' ||
      fund.ticker === 'TRATX') &&
      !selectedAgreementIsIRA,
    FundNoteAdd.RETIREMENT_INCOME_FOR_NON_IRA
  );

  updateFundNote(
    fund,
    (fund.ticker === 'TRLAX' ||
      fund.ticker === 'TRABX' ||
      fund.ticker === 'TRAVX' ||
      fund.ticker === 'TRATX') &&
      selectedAgreementIsIRA,
    FundNoteAdd.RETIREMENT_INCOME_FOR_IRA,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.summit,
    FundNoteAdd.SUMMIT,
    classification === RETIREMENT ? DISABLE_ON_MATCH : null
  );

  return updateSharedAddFundNotesForFund(
    fund,
    newFunds,
    selectedRegistrationType
  );
}

/**
 * Exchange-specific, updates the passed in ALL fund object with applicable notes and disables as appropriate.
 *
 * @param {AllFund} fund
 * @param {UserFund[]} newFunds
 * @param {object} selectedRegistrationType
 * @returns {AllFund} Same fund object is returned to simplify chaining
 */
export function updateExchangeAddFundNotesForFund(
  fund,
  newFunds,
  selectedRegistrationType
) {
  if (!selectedRegistrationType) {
    return fund;
  }

  updateFundNote(
    fund,
    fund.ticker === 'TRLAX' ||
      fund.ticker === 'TRABX' ||
      fund.ticker === 'TRAVX' ||
      fund.ticker === 'TRATX',
    FundNoteAdd.RETIREMENT_INCOME_FOR_NON_IRA
  ); // triggers appropriate messaging, even if in Exchange it is an IRA

  return updateSharedAddFundNotesForFund(
    fund,
    newFunds,
    selectedRegistrationType
  );
}
